import type { LandingPageButtonStyleId, LandingPageFontId, LandingPageSettings } from "./landing"

export const landingPageStyleIds = [
	"clean",
	"minimal",
	"warm",
	"professional",
	"modern",
	"bold",
	"natural",
	"midnight",
] as const
export type LandingPageStyleId = (typeof landingPageStyleIds)[number]

export const landingPagePatternIds = ["none", "dots", "grid"] as const
export type LandingPagePatternId = (typeof landingPagePatternIds)[number]

// Patterns are drawn from the foreground color so the same pattern reads on a light and a dark palette.
export const landingPagePatterns: Record<LandingPagePatternId, { image: string; size: string }> = {
	none: { image: "none", size: "auto" },
	dots: {
		image: "radial-gradient(color-mix(in srgb, var(--landing-foreground), transparent 88%) 1px, transparent 1.5px)",
		size: "22px 22px",
	},
	grid: {
		image: [
			"linear-gradient(color-mix(in srgb, var(--landing-foreground), transparent 94%) 1px, transparent 1px)",
			"linear-gradient(90deg, color-mix(in srgb, var(--landing-foreground), transparent 94%) 1px, transparent 1px)",
		].join(", "),
		size: "36px 36px",
	},
}

export type LandingPagePalette = {
	accent: string
	background: string
	surface: string
	surfaceMuted: string
	foreground: string
	foregroundMuted: string
	foregroundSubtle: string
	border: string
	headerBackground: string
	headerForeground: string
	footerBackground: string
	footerForeground: string
	footerForegroundMuted: string
}

export type LandingPageStyle = {
	label: string
	description: string
	fontId: LandingPageFontId
	buttonStyleId: LandingPageButtonStyleId
	pattern: LandingPagePatternId
	palette: LandingPagePalette
}

// The clean style is the look every website had before styles existed, so its palette has to keep
// matching the Tailwind grays the blocks used to hardcode.
export const landingPageStyles: Record<LandingPageStyleId, LandingPageStyle> = {
	clean: {
		label: "Clean",
		description: "White pages with soft gray surfaces and a dark footer.",
		fontId: "modern",
		buttonStyleId: "pill",
		pattern: "none",
		palette: {
			accent: "#A155F5",
			background: "#ffffff",
			surface: "#ffffff",
			surfaceMuted: "#f9fafb",
			foreground: "#111827",
			foregroundMuted: "#4b5563",
			foregroundSubtle: "#6b7280",
			border: "#e5e7eb",
			headerBackground: "#ffffff",
			headerForeground: "#111827",
			footerBackground: "#111827",
			footerForeground: "#ffffff",
			footerForegroundMuted: "#9ca3af",
		},
	},
	minimal: {
		label: "Minimal",
		description: "Black on white, square corners and plenty of air.",
		fontId: "inter",
		buttonStyleId: "square",
		pattern: "none",
		palette: {
			accent: "#0a0a0a",
			background: "#ffffff",
			surface: "#ffffff",
			surfaceMuted: "#f5f5f5",
			foreground: "#0a0a0a",
			foregroundMuted: "#525252",
			foregroundSubtle: "#737373",
			border: "#e5e5e5",
			headerBackground: "#ffffff",
			headerForeground: "#0a0a0a",
			footerBackground: "#fafafa",
			footerForeground: "#0a0a0a",
			footerForegroundMuted: "#737373",
		},
	},
	warm: {
		label: "Warm",
		description: "Cream pages, terracotta accents and a friendly serif.",
		fontId: "friendly",
		buttonStyleId: "rounded",
		pattern: "none",
		palette: {
			accent: "#c2410c",
			background: "#fdf8f1",
			surface: "#fffdf9",
			surfaceMuted: "#f7ecdd",
			foreground: "#3b2a1e",
			foregroundMuted: "#6b5443",
			foregroundSubtle: "#927a69",
			border: "#ead9c4",
			headerBackground: "#fdf8f1",
			headerForeground: "#3b2a1e",
			footerBackground: "#3b2a1e",
			footerForeground: "#fdf8f1",
			footerForegroundMuted: "#c9b3a0",
		},
	},
	professional: {
		label: "Professional",
		description: "A navy header, slate surfaces and a trustworthy blue.",
		fontId: "classic",
		buttonStyleId: "rounded",
		pattern: "none",
		palette: {
			accent: "#1d4ed8",
			background: "#f8fafc",
			surface: "#ffffff",
			surfaceMuted: "#eef2f7",
			foreground: "#0f1f3d",
			foregroundMuted: "#475569",
			foregroundSubtle: "#64748b",
			border: "#dbe2ea",
			headerBackground: "#0f1f3d",
			headerForeground: "#ffffff",
			footerBackground: "#0f1f3d",
			footerForeground: "#ffffff",
			footerForegroundMuted: "#94a3b8",
		},
	},
	modern: {
		label: "Modern",
		description: "Cool off-white with a faint grid and an electric accent.",
		fontId: "technical",
		buttonStyleId: "soft",
		pattern: "grid",
		palette: {
			accent: "#4f46e5",
			background: "#fafafa",
			surface: "#ffffff",
			surfaceMuted: "#f1f5f9",
			foreground: "#18181b",
			foregroundMuted: "#52525b",
			foregroundSubtle: "#71717a",
			border: "#e4e4e7",
			headerBackground: "#ffffff",
			headerForeground: "#18181b",
			footerBackground: "#18181b",
			footerForeground: "#fafafa",
			footerForegroundMuted: "#a1a1aa",
		},
	},
	bold: {
		label: "Bold",
		description: "Sunny yellow pages, hard edges and offset shadows.",
		fontId: "poppins",
		buttonStyleId: "bold",
		pattern: "dots",
		palette: {
			accent: "#e11d48",
			background: "#fff7d6",
			surface: "#ffffff",
			surfaceMuted: "#fdeeb3",
			foreground: "#1c1917",
			foregroundMuted: "#44403c",
			foregroundSubtle: "#78716c",
			border: "#ecdba3",
			headerBackground: "#fff7d6",
			headerForeground: "#1c1917",
			footerBackground: "#1c1917",
			footerForeground: "#fff7d6",
			footerForegroundMuted: "#d6cfc4",
		},
	},
	natural: {
		label: "Natural",
		description: "Sage green and soft stone for calm, grounded brands.",
		fontId: "editorial",
		buttonStyleId: "pill",
		pattern: "none",
		palette: {
			accent: "#3d7a55",
			background: "#f5f7f2",
			surface: "#ffffff",
			surfaceMuted: "#e9eee4",
			foreground: "#1f2a22",
			foregroundMuted: "#4d5c51",
			foregroundSubtle: "#6f7f73",
			border: "#d9e1d4",
			headerBackground: "#f5f7f2",
			headerForeground: "#1f2a22",
			footerBackground: "#1f2a22",
			footerForeground: "#f5f7f2",
			footerForegroundMuted: "#a3b3a7",
		},
	},
	midnight: {
		label: "Midnight",
		description: "A dark theme with deep navy surfaces and a glowing accent.",
		fontId: "modern",
		buttonStyleId: "pill",
		pattern: "none",
		palette: {
			accent: "#8b5cf6",
			background: "#0b1120",
			surface: "#111a2e",
			surfaceMuted: "#172036",
			foreground: "#f1f5f9",
			foregroundMuted: "#b6c2d4",
			foregroundSubtle: "#8291a8",
			border: "#243049",
			headerBackground: "#0b1120",
			headerForeground: "#f1f5f9",
			footerBackground: "#060b16",
			footerForeground: "#f1f5f9",
			footerForegroundMuted: "#8291a8",
		},
	},
}

export type LandingPageBrandColor = { value: string; isCustom: boolean }

/**
 * The accent the website renders with. A brand color only overrides the style's own accent when the
 * creator has actually set one — the academy's default purple would otherwise override every style.
 */
export function resolveLandingPageAccentColor(
	brandColor: LandingPageBrandColor,
	settings: Pick<LandingPageSettings, "styleId" | "usesBrandColor">,
): string {
	const style = landingPageStyles[settings.styleId]
	if (!settings.usesBrandColor || !brandColor.isCustom) return style.palette.accent

	return normalizeHexColor(brandColor.value) ?? style.palette.accent
}

/**
 * Picking a style applies its font and button pairing, unless the same change names them
 * explicitly. Both stay editable on their own afterwards.
 */
export function applyLandingPageStylePreset<
	T extends { styleId?: LandingPageStyleId; fontId?: LandingPageFontId; buttonStyleId?: LandingPageButtonStyleId },
>(change: T): T {
	if (change.styleId === undefined) return change

	const style = landingPageStyles[change.styleId]
	return {
		...change,
		fontId: change.fontId ?? style.fontId,
		buttonStyleId: change.buttonStyleId ?? style.buttonStyleId,
	}
}

function normalizeHexColor(color: string): string | null {
	const trimmed = color.trim()
	if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed
	if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
		return `#${trimmed
			.slice(1)
			.split("")
			.map(character => character + character)
			.join("")}`
	}
	return null
}
